// Pure functions that turn the onboarding survey into a concrete plan.
// All calculations are deterministic — no async / no API calls — so the
// dashboard card can render instantly from the cached `user` object.

const ACTIVITY_MULTIPLIERS = {
  sedentary: 1.2,
  lightly_active: 1.375,
  active: 1.55,
  very_active: 1.725,
};

// Calorie adjustment vs maintenance, per primary goal.
const GOAL_ADJUSTMENTS = {
  lose_fat: -500,
  build_muscle: 300,
  recomp: -150,
  athletic: 0,
  general: 0,
};

// Protein target in grams per pound of body weight, per goal.
const PROTEIN_PER_LB = {
  lose_fat: 1.0,
  build_muscle: 0.9,
  recomp: 1.0,
  athletic: 0.85,
  general: 0.7,
};

// Recommended training days/week, by experience level.
const TRAINING_DAYS_BY_EXPERIENCE = {
  beginner: { min: 3, max: 4 },
  intermediate: { min: 4, max: 5 },
  advanced: { min: 5, max: 6 },
};

// Training "focus" label per goal.
const TRAINING_FOCUS = {
  lose_fat: "Strength + 2-3× cardio sessions",
  build_muscle: "Hypertrophy split (push/pull/legs)",
  recomp: "Strength bias + light cardio",
  athletic: "Athletic conditioning + power lifts",
  general: "Balanced full-body training",
};

const GOAL_LABEL = {
  lose_fat: "Fat loss",
  build_muscle: "Muscle gain",
  recomp: "Recomposition",
  athletic: "Athletic performance",
  general: "General fitness",
};

function lbToKg(lb) {
  return lb / 2.205;
}

function inchToCm(inch) {
  return inch * 2.54;
}

function round5(n) {
  return Math.round(n / 5) * 5;
}

/**
 * Returns a plan object, or `null` if the user hasn't completed enough of
 * onboarding for the math to make sense.
 *
 * If `user.plan_overrides` contains any fields, those replace the auto-computed
 * values one-for-one. The `overrides` field on the returned plan lists which
 * keys came from user input so the UI can flag them.
 *
 *  plan = {
 *    maintenance, target_calories, goal_delta, goal_label,
 *    protein_g, fats_g, carbs_g,
 *    training_days, training_focus, checkin_cadence,
 *    baseline_maintenance,         // null if no baseline_weight stored
 *    maintenance_delta,            // current - baseline; null if no baseline
 *    overrides: ['target_calories', ...]
 *  }
 */
export function computePlan(user) {
  if (!user) return null;
  const weight = Number(user.current_weight);
  const heightIn = Number(user.height); // stored as inches in onboarding
  const age = Number(user.age);
  const gender = user.gender;
  const goal = user.primary_goal;
  const activity = user.activity_level;
  const experience = user.training_experience;
  if (!weight || !heightIn || !age || !goal || !activity) return null;

  const weightKg = (user.weight_unit === "kg" ? weight : lbToKg(weight));
  const heightCm = (user.height_unit === "metric" ? Number(user.height_cm) || inchToCm(heightIn) : inchToCm(heightIn));
  const weightLb = user.weight_unit === "kg" ? weight * 2.205 : weight;

  // Mifflin–St Jeor BMR
  let bmr;
  if (gender === "female") bmr = 10 * weightKg + 6.25 * heightCm - 5 * age - 161;
  else if (gender === "male") bmr = 10 * weightKg + 6.25 * heightCm - 5 * age + 5;
  else bmr = 10 * weightKg + 6.25 * heightCm - 5 * age - 78; // gender-neutral midpoint

  const multiplier = ACTIVITY_MULTIPLIERS[activity] ?? 1.4;
  const maintenance = Math.round(bmr * multiplier);

  const goalDelta = GOAL_ADJUSTMENTS[goal] ?? 0;
  const autoTargetCalories = Math.max(1200, round5(maintenance + goalDelta));

  const autoProteinG = Math.round(weightLb * (PROTEIN_PER_LB[goal] ?? 0.8));
  const autoFatsG = Math.round((autoTargetCalories * 0.25) / 9);
  const autoCarbsG = Math.max(40, Math.round((autoTargetCalories - autoProteinG * 4 - autoFatsG * 9) / 4));

  const dayRange = TRAINING_DAYS_BY_EXPERIENCE[experience] || { min: 3, max: 5 };
  const autoTrainingDays =
    dayRange.min === dayRange.max
      ? `${dayRange.min} days/week`
      : `${dayRange.min}–${dayRange.max} days/week`;
  const autoTrainingFocus = TRAINING_FOCUS[goal] || "Balanced training";

  // Apply user overrides — only non-null/empty values replace the auto value.
  const o = user.plan_overrides || {};
  const isSet = (v) => v !== null && v !== undefined && v !== "";
  const overrides = [];
  const pickOverride = (key, autoVal) => {
    if (isSet(o[key])) {
      overrides.push(key);
      return typeof autoVal === "number" ? Number(o[key]) : o[key];
    }
    return autoVal;
  };

  // Baseline-maintenance delta (lets users see if their TDEE has shifted since
  // they set their plan, due to weight changes).
  let baselineMaintenance = null;
  let maintenanceDelta = null;
  const baselineWeight = Number(user.baseline_weight);
  if (baselineWeight && baselineWeight !== weight) {
    const baseKg = user.weight_unit === "kg" ? baselineWeight : lbToKg(baselineWeight);
    let baseBmr;
    if (gender === "female") baseBmr = 10 * baseKg + 6.25 * heightCm - 5 * age - 161;
    else if (gender === "male") baseBmr = 10 * baseKg + 6.25 * heightCm - 5 * age + 5;
    else baseBmr = 10 * baseKg + 6.25 * heightCm - 5 * age - 78;
    baselineMaintenance = Math.round(baseBmr * multiplier);
    maintenanceDelta = maintenance - baselineMaintenance;
  }

  return {
    maintenance,
    target_calories: pickOverride("target_calories", autoTargetCalories),
    goal_delta: goalDelta,
    goal_label: GOAL_LABEL[goal] || goal,
    protein_g: pickOverride("protein_g", autoProteinG),
    fats_g: pickOverride("fats_g", autoFatsG),
    carbs_g: pickOverride("carbs_g", autoCarbsG),
    training_days: pickOverride("training_days", autoTrainingDays),
    training_focus: pickOverride("training_focus", autoTrainingFocus),
    checkin_cadence: "Weekly check-in · Sunday",
    baseline_maintenance: baselineMaintenance,
    maintenance_delta: maintenanceDelta,
    overrides,
    // Expose the auto values so the edit dialog can show "reset to default".
    auto: {
      target_calories: autoTargetCalories,
      protein_g: autoProteinG,
      fats_g: autoFatsG,
      carbs_g: autoCarbsG,
      training_days: autoTrainingDays,
      training_focus: autoTrainingFocus,
    },
  };
}
